import React from "react";
import {
    Bug,
    Building2,
    Check,
    Rat,
    ShieldCheck,
    SprayCan,
} from "lucide-react";

type PointsTilesIconComponent = React.ComponentType<{
    className?: string;
    size?: number;
    strokeWidth?: number;
    "aria-hidden"?: boolean;
}>;

export type PointsTilesImage = {
    src?: string;
    url?: string;
    original?: string;
    thumbnail?: string;
    alt?: string;
    title?: string;
    originalAlt?: string;
    thumbnailAlt?: string;
    objectPosition?: string;
};

export type PointsTilesTile = {
    title: string;
    points: string[];
    imageUrl?: string;
    id?: string | number;
    image?: PointsTilesImage;
    imageAlt?: string;
    imagePosition?: string;
    icon?: React.ReactNode;
    iconSrc?: string;
    iconAlt?: string;
    accentColor?: string;
    accentSoftColor?: string;
    accentDarkColor?: string;
};

export type PointsTilesLayout = "responsive" | "split" | "stacked";
export type PointsTilesVariant = "section" | "cards";

export type PointsTilesProps = {
    pointsData: PointsTilesTile[];
    pointsClasses?: string;
    pointTitleClasses?: string;
    eyebrow?: React.ReactNode;
    title?: React.ReactNode;
    description?: React.ReactNode;
    className?: string;
    headerClassName?: string;
    gridClassName?: string;
    cardClassName?: string;
    layout?: PointsTilesLayout;
    variant?: PointsTilesVariant;
    backgroundColor?: string;
    maxWidth?: number | string;
    imageHeight?: number | string;
    imageHeightMobile?: number | string;
    ariaLabel?: string;
};

type PointsTilesStyle = React.CSSProperties & Record<`--${string}`, string>;

const tilePalettes = [
    {
        color: "#3f9b43",
        soft: "#dff2d3",
        dark: "#26752e",
    },
    {
        color: "#d98024",
        soft: "#ffe0aa",
        dark: "#a65612",
    },
];

const pointsTilesCss = `
.points-tiles {
  --points-tiles-bg: #fbfaf6;
  --points-tiles-text: #102433;
  --points-tiles-muted: #4c5968;
  --points-tiles-card-bg: #ffffff;
  --points-tiles-card-border: rgba(16, 36, 51, 0.08);
  --points-tiles-shadow: 0 18px 42px rgba(20, 31, 42, 0.11);
  --points-tiles-check: #43a247;
  --points-tiles-max-width: 1280px;
  --points-tiles-image-height: 260px;
  --points-tiles-image-height-mobile: 222px;
  position: relative;
  width: 100%;
  overflow: hidden;
  background: var(--points-tiles-bg);
  color: var(--points-tiles-text);
  font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
}
.points-tiles,
.points-tiles * {
  box-sizing: border-box;
}
.points-tiles::before,
.points-tiles::after {
  content: "";
  position: absolute;
  pointer-events: none;
  opacity: 0.58;
}
.points-tiles::before {
  top: 34px;
  right: 44px;
  width: 158px;
  height: 158px;
  background-image: radial-gradient(circle, rgba(16, 36, 51, 0.12) 2px, transparent 2px);
  background-size: 18px 18px;
}
.points-tiles::after {
  bottom: 32px;
  left: 20px;
  width: 116px;
  height: 116px;
  background-image: radial-gradient(circle, rgba(63, 155, 67, 0.15) 2px, transparent 2px);
  background-size: 18px 18px;
}
.points-tiles--cards {
  overflow: visible;
  background: transparent;
}
.points-tiles--cards::before,
.points-tiles--cards::after {
  display: none;
}
.points-tiles-shell {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: var(--points-tiles-max-width);
  margin: 0 auto;
  padding: 64px 24px 56px;
}
.points-tiles--cards .points-tiles-shell {
  max-width: none;
  padding: 0;
}
.points-tiles-header {
  max-width: 920px;
  margin: 0 auto 34px;
  text-align: center;
}
.points-tiles-eyebrow {
  display: inline-flex;
  max-width: 100%;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin: 0 0 16px;
  color: #33833a;
  font-size: 15px;
  line-height: 1.2;
  font-weight: 800;
  letter-spacing: 0;
  text-transform: uppercase;
}
.points-tiles-eyebrow::before,
.points-tiles-eyebrow::after {
  content: "";
  display: block;
  width: 42px;
  height: 2px;
  flex: 0 0 auto;
  border-radius: 999px;
  background: currentColor;
}
.points-tiles-title {
  max-width: 100%;
  margin: 0;
  color: var(--points-tiles-text);
  font-size: 56px;
  line-height: 1.06;
  font-weight: 850;
  letter-spacing: 0;
  overflow-wrap: anywhere;
}
.points-tiles-description {
  max-width: 860px;
  margin: 18px auto 0;
  color: var(--points-tiles-muted);
  font-size: 18px;
  line-height: 1.55;
  font-weight: 500;
  letter-spacing: 0;
}
.points-tiles-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 30px;
}
.points-tiles-card {
  --points-tiles-tile-accent: #3f9b43;
  --points-tiles-tile-accent-soft: #dff2d3;
  --points-tiles-tile-accent-dark: #26752e;
  position: relative;
  display: grid;
  grid-template-columns: minmax(230px, 0.93fr) minmax(280px, 1.06fr);
  min-width: 0;
  min-height: 294px;
  overflow: hidden;
  border: 1px solid var(--points-tiles-card-border);
  border-radius: 8px;
  background: var(--points-tiles-card-bg);
  box-shadow: var(--points-tiles-shadow);
  isolation: isolate;
}
.points-tiles-card::before {
  content: "";
  position: absolute;
  z-index: 2;
  top: 0;
  right: 0;
  left: 0;
  height: 5px;
  background: linear-gradient(90deg, var(--points-tiles-tile-accent) 0%, var(--points-tiles-tile-accent-soft) 100%);
}
.points-tiles-media {
  position: relative;
  min-width: 0;
  min-height: var(--points-tiles-image-height);
  overflow: hidden;
  background: linear-gradient(135deg, #edf2ee 0%, #f8f3ec 100%);
}
.points-tiles-image,
.points-tiles-image-fallback {
  display: block;
  width: 100%;
  height: 100%;
  min-height: var(--points-tiles-image-height);
}
.points-tiles-image {
  object-fit: cover;
  object-position: var(--points-tiles-image-position, center);
  transform: scale(1);
  transition: transform 360ms ease, filter 360ms ease;
}
.points-tiles-card:hover .points-tiles-image {
  transform: scale(1.035);
  filter: saturate(1.05);
}
.points-tiles-image-fallback {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 26px;
  color: rgba(16, 36, 51, 0.58);
  font-size: 15px;
  line-height: 1.4;
  font-weight: 800;
  text-align: center;
}
.points-tiles-icon {
  position: absolute;
  top: 24px;
  left: 24px;
  display: inline-flex;
  width: 58px;
  height: 58px;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(16, 36, 51, 0.08);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.95);
  color: var(--points-tiles-tile-accent);
  box-shadow: 0 8px 20px rgba(16, 36, 51, 0.14);
}
.points-tiles-icon img {
  display: block;
  width: 30px;
  height: 30px;
  object-fit: contain;
}
.points-tiles-custom-icon {
  display: inline-flex;
  width: 30px;
  height: 30px;
  align-items: center;
  justify-content: center;
}
.points-tiles-custom-icon svg {
  width: 100%;
  height: 100%;
}
.points-tiles-content {
  display: flex;
  min-width: 0;
  flex-direction: column;
  justify-content: center;
  padding: 48px 34px 34px;
}
.points-tiles-card-title {
  max-width: 100%;
  margin: 0;
  color: var(--points-tiles-text);
  font-size: 24px;
  line-height: 1.18;
  font-weight: 800;
  letter-spacing: 0;
  overflow-wrap: anywhere;
}
.points-tiles-title-rule {
  display: block;
  width: 40px;
  height: 3px;
  margin-top: 14px;
  border-radius: 999px;
  background: var(--points-tiles-tile-accent);
}
.points-tiles-list {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 11px;
  margin: 24px 0 0;
  padding: 0;
  list-style: none;
}
.points-tiles-point {
  display: grid;
  grid-template-columns: 20px minmax(0, 1fr);
  min-width: 0;
  align-items: start;
  gap: 10px;
  color: #4c5968;
  font-size: 15px;
  line-height: 1.42;
  font-weight: 500;
  letter-spacing: 0;
}
.points-tiles-check {
  display: inline-flex;
  width: 17px;
  height: 17px;
  align-items: center;
  justify-content: center;
  margin-top: 2px;
  border-radius: 999px;
  background: var(--points-tiles-check);
  color: #ffffff;
}
.points-tiles-point-text {
  min-width: 0;
  color: inherit;
  font: inherit;
  overflow-wrap: anywhere;
}
.points-tiles--stacked .points-tiles-card {
  grid-template-columns: minmax(0, 1fr);
}
.points-tiles--stacked .points-tiles-media {
  min-height: var(--points-tiles-image-height-mobile);
}
.points-tiles--stacked .points-tiles-image,
.points-tiles--stacked .points-tiles-image-fallback {
  min-height: var(--points-tiles-image-height-mobile);
}
.points-tiles--stacked .points-tiles-content {
  padding: 28px 34px 32px;
}
.points-tiles--stacked .points-tiles-list {
  grid-template-columns: repeat(2, minmax(0, 1fr));
  column-gap: 34px;
}
@media (max-width: 1180px) {
  .points-tiles-grid {
    grid-template-columns: minmax(0, 1fr);
  }
  .points-tiles-card {
    max-width: 980px;
    width: 100%;
    margin: 0 auto;
  }
}
@media (max-width: 767px) {
  .points-tiles-shell {
    padding: 48px 16px 42px;
  }
  .points-tiles-header {
    margin-bottom: 28px;
  }
  .points-tiles-eyebrow {
    gap: 12px;
    font-size: 13px;
  }
  .points-tiles-eyebrow::before,
  .points-tiles-eyebrow::after {
    width: 32px;
  }
  .points-tiles-title {
    font-size: 42px;
    line-height: 1.08;
  }
  .points-tiles-description {
    margin-top: 14px;
    font-size: 17px;
    line-height: 1.45;
  }
  .points-tiles-grid {
    gap: 22px;
  }
  .points-tiles-card,
  .points-tiles--split .points-tiles-card {
    grid-template-columns: minmax(0, 1fr);
  }
  .points-tiles-media {
    min-height: var(--points-tiles-image-height-mobile);
  }
  .points-tiles-image,
  .points-tiles-image-fallback {
    min-height: var(--points-tiles-image-height-mobile);
  }
  .points-tiles-icon {
    top: 18px;
    left: 18px;
    width: 52px;
    height: 52px;
  }
  .points-tiles-content {
    padding: 24px 28px 28px;
  }
  .points-tiles-card-title {
    font-size: 24px;
  }
  .points-tiles-list {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    column-gap: 28px;
    row-gap: 12px;
    margin-top: 22px;
  }
}
@media (max-width: 560px) {
  .points-tiles-shell {
    padding: 42px 14px 36px;
  }
  .points-tiles-title {
    font-size: 34px;
  }
  .points-tiles-description {
    font-size: 16px;
  }
  .points-tiles-card {
    border-radius: 8px;
  }
  .points-tiles-content {
    padding: 22px 20px 24px;
  }
  .points-tiles-list,
  .points-tiles--stacked .points-tiles-list {
    grid-template-columns: minmax(0, 1fr);
  }
}
`;

const toCssSize = (value?: number | string) => {
    if (typeof value === "number") {
        return `${value}px`;
    }

    return value;
};

const joinClassNames = (...classNames: Array<string | false | null | undefined>) =>
    classNames.filter(Boolean).join(" ");

const getImageUrl = (tile: PointsTilesTile) =>
    tile.imageUrl ||
    tile.image?.src ||
    tile.image?.url ||
    tile.image?.original ||
    tile.image?.thumbnail;

const getImageAlt = (tile: PointsTilesTile) =>
    tile.imageAlt ||
    tile.image?.alt ||
    tile.image?.originalAlt ||
    tile.image?.thumbnailAlt ||
    tile.image?.title ||
    tile.title;

const getTilePalette = (tile: PointsTilesTile, index: number) => {
    const title = tile.title.toLowerCase();

    if (/(rodent|rat|mouse|mice|termite|pre-construction)/.test(title)) {
        return tilePalettes[1];
    }

    if (/(commercial|industrial|restaurant|warehouse|kitchen)/.test(title)) {
        return tilePalettes[0];
    }

    return tilePalettes[index % tilePalettes.length];
};

const getTileIcon = (tile: PointsTilesTile): PointsTilesIconComponent => {
    const title = tile.title.toLowerCase();

    if (/(rodent|rat|mouse|mice)/.test(title)) {
        return Rat;
    }

    if (/(commercial|industrial|restaurant|warehouse|kitchen)/.test(title)) {
        return Building2;
    }

    if (/(termite|ant|spider|cockroach|silverfish|insect)/.test(title)) {
        return Bug;
    }

    if (/(spray|treatment|saniti|clean)/.test(title)) {
        return SprayCan;
    }

    return ShieldCheck;
};

const renderTileIcon = (tile: PointsTilesTile, Icon: PointsTilesIconComponent) => {
    if (tile.iconSrc) {
        return <img src={tile.iconSrc} alt={tile.iconAlt || ""} loading="lazy" />;
    }

    if (tile.icon) {
        return <span className="points-tiles-custom-icon">{tile.icon}</span>;
    }

    return <Icon className="points-tiles-lucide-icon" size={29} strokeWidth={2.3} aria-hidden />;
};

const PointsTiles = ({
    pointsData,
    pointsClasses,
    pointTitleClasses,
    eyebrow,
    title,
    description,
    className,
    headerClassName,
    gridClassName,
    cardClassName,
    layout = "responsive",
    variant = "section",
    backgroundColor,
    maxWidth,
    imageHeight,
    imageHeightMobile,
    ariaLabel,
}: PointsTilesProps) => {
    const RootTag = variant === "section" ? "section" : "div";
    const hasHeader = Boolean(eyebrow || title || description);
    const rootStyle: PointsTilesStyle = {
        ...(backgroundColor ? { "--points-tiles-bg": backgroundColor } : {}),
        ...(maxWidth ? { "--points-tiles-max-width": toCssSize(maxWidth) } : {}),
        ...(imageHeight ? { "--points-tiles-image-height": toCssSize(imageHeight) } : {}),
        ...(imageHeightMobile
            ? { "--points-tiles-image-height-mobile": toCssSize(imageHeightMobile) }
            : {}),
    };

    return (
        <RootTag
            className={joinClassNames(
                "points-tiles",
                variant === "cards" && "points-tiles--cards",
                layout === "split" && "points-tiles--split",
                layout === "stacked" && "points-tiles--stacked",
                className
            )}
            style={rootStyle}
            aria-label={ariaLabel}
        >
            <style>{pointsTilesCss}</style>

            <div className="points-tiles-shell">
                {hasHeader && (
                    <header className={joinClassNames("points-tiles-header", headerClassName)}>
                        {eyebrow && <p className="points-tiles-eyebrow">{eyebrow}</p>}
                        {title && <h2 className="points-tiles-title">{title}</h2>}
                        {description && <p className="points-tiles-description">{description}</p>}
                    </header>
                )}

                <div className={joinClassNames("points-tiles-grid", gridClassName)}>
                    {pointsData.map((pointData: PointsTilesTile, index: number) => {
                        const palette = getTilePalette(pointData, index);
                        const imageUrl = getImageUrl(pointData);
                        const Icon = getTileIcon(pointData);
                        const tileStyle: PointsTilesStyle = {
                            "--points-tiles-tile-accent": pointData.accentColor || palette.color,
                            "--points-tiles-tile-accent-soft":
                                pointData.accentSoftColor || palette.soft,
                            "--points-tiles-tile-accent-dark":
                                pointData.accentDarkColor || palette.dark,
                            ...(pointData.imagePosition || pointData.image?.objectPosition
                                ? {
                                      "--points-tiles-image-position":
                                          pointData.imagePosition ||
                                          pointData.image?.objectPosition ||
                                          "center",
                                  }
                                : {}),
                        };

                        return (
                            <article
                                key={pointData.id || `${pointData.title}-${index}`}
                                className={joinClassNames("points-tiles-card", cardClassName)}
                                style={tileStyle}
                            >
                                <div className="points-tiles-media">
                                    {imageUrl ? (
                                        <img
                                            src={imageUrl}
                                            alt={getImageAlt(pointData)}
                                            className="points-tiles-image"
                                            loading="lazy"
                                        />
                                    ) : (
                                        <div className="points-tiles-image-fallback">
                                            <span>{pointData.title}</span>
                                        </div>
                                    )}

                                    <span className="points-tiles-icon" aria-hidden="true">
                                        {renderTileIcon(pointData, Icon)}
                                    </span>
                                </div>

                                <div className="points-tiles-content">
                                    <h3
                                        className={joinClassNames(
                                            "points-tiles-card-title",
                                            pointTitleClasses
                                        )}
                                    >
                                        {pointData.title}
                                    </h3>
                                    <span className="points-tiles-title-rule" aria-hidden="true" />

                                    <ul className="points-tiles-list">
                                        {pointData.points.map((point, pointIndex) => (
                                            <li
                                                key={`${point}-${pointIndex}`}
                                                className={joinClassNames(
                                                    "points-tiles-point",
                                                    pointsClasses
                                                )}
                                            >
                                                <span
                                                    className="points-tiles-check"
                                                    aria-hidden="true"
                                                >
                                                    <Check size={12} strokeWidth={3.4} />
                                                </span>
                                                <span className="points-tiles-point-text">
                                                    {point}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </article>
                        );
                    })}
                </div>
            </div>
        </RootTag>
    );
};

export default PointsTiles;
